'use client';
/**
 * « DÉCLARER FAITE » — UNE fenêtre, UNE porte (spec 002, récit 3, R14 — 04/11/2026).
 *
 * Six chemins menaient à la déclaration d'une échéance, sous cinq noms et deux
 * règles de date. Il n'en reste qu'un : chaque point d'entrée (Mon jour, le
 * calendrier du tableau de bord, la liste des échéances, l'onglet Maintenance
 * d'un appareil) appelle `ouvrirDeclaration(ligne@date)` du magasin, et cette
 * fenêtre — montée une fois, par le Shell (`HoteDeclaration`) — fait le reste.
 * Les règles (date, échéance à venir, bouton direct, NC proposée) vivent dans
 * `lib/declaration-maintenance.ts`, éprouvé par
 * `backend/tests/maintenance-declaration.test.js`.
 *
 * Extraite de l'ancienne fenêtre du registre (`Maintenances.tsx`) : ordre du
 * contrat (`specs/002-refonte-maintenance/contracts/ecrans.md`) — échéance →
 * supports → réalisée le → opérations → commentaire → anomalie (et NC) →
 * pièces → « Déclarer faite ».
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useGed, type DemandeDeclaration } from '@/lib/store';
import { fmtDate, aujourdhuiIso } from '@/lib/meta';
import { I, Modale, inputStyle, Selecteur, useBusy } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import { lbl } from '@/components/screens/ressources/commun';
import { ListeSupports } from '@/components/screens/ressources/SupportsMaintenance';
import { DeclareForm } from '@/components/screens/NC';
import { TYPES_DOCUMENT } from '@/lib/documents-maintenance';
import { supportsDeLaFenetre } from '@/lib/supports-maintenance';
import {
  datePreremplie, declarable, designe, ncPreremplie, proposerNC, peutClore, refusMotifCloture,
} from '@/lib/declaration-maintenance';
import { peutGererParc } from '@/lib/droits-parc';
import type { MaintenanceLigne, MaintenanceOccurrence } from '@/lib/types';

/**
 * Déclarer une échéance faite.
 *
 * Le formulaire fait déclarer les opérations UNE PAR UNE, faite ou non faite,
 * parce qu'une opération non faite n'est pas une case oubliée : l'acte la
 * conserve telle quelle, avec son motif (28/10/2026).
 */
export function DeclarerFaite({ occ, ligne, operationsConnues, onFerme, onFait }: {
  occ: MaintenanceOccurrence;
  ligne: MaintenanceLigne | undefined;
  /** Les opérations, quand l'appelant les tient d'ailleurs que du plan.
   *
   *  ⚠️ Le mémo du jour (`MonJour`) est dans ce cas : il reçoit ses occurrences
   *  de `/ged/rh/mon-jour`, qui les sert à TOUT LE MONDE, là où le plan est
   *  derrière la section « maintenances ». Sans cette porte, un technicien dont
   *  le paramétrage ferme cette section verrait un formulaire SANS AUCUNE
   *  OPÉRATION et les déclarerait toutes non faites sans qu'on les lui ait
   *  montrées. */
  operationsConnues?: string[];
  onFerme: () => void;
  onFait: () => void;
}) {
  const { smqData, joindreDocumentActe, session } = useGed();
  const personnesRegistre = useGed((s) => s.maintenances?.personnes);
  const { busy, run } = useBusy();
  const gereParc = peutGererParc(session.roles);
  /* ── LES TROIS ISSUES D'UNE ÉCHÉANCE (spec 002, récit 4) ───────────────────
     · `faite` : la déclaration habituelle ;
     · `non_realisee` (T076, règle Q5) : close avec un motif — RQ, Métrologie,
       responsable de la maintenance ; elle rejoint le registre des non réalisées ;
     · la REPRISE (T078, EF-015) est une variante de « faite » : saisie a
       posteriori, éventuellement pour autrui, motif exigé — RQ et Métrologie. */
  const [issue, setIssue] = useState<'faite' | 'non_realisee'>('faite');
  const [motifNonRealisee, setMotifNonRealisee] = useState('');
  const clotureOuverte = peutClore(occ, session.key, gereParc, aujourdhuiIso());
  const [reprise, setReprise] = useState(false);
  const [repriseParKey, setRepriseParKey] = useState('');
  const [motifReprise, setMotifReprise] = useState('');
  /* Les personnes du registre (initiales du calendrier), une fois chacune, par nom. */
  const personnes = useMemo(() => {
    const vus = new Map<string, string>();
    for (const [cle, p] of Object.entries(personnesRegistre || {})) if (p?.nom && !vus.has(p.nom)) vus.set(p.nom, cle);
    return Array.from(vus, ([nom, cle]) => ({ nom, cle })).sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
  }, [personnesRegistre]);
  const operations = ligne?.operations || operationsConnues || [];
  /* LES SUPPORTS DE CETTE MAINTENANCE (spec 002, récit 2) : ceux reliés à la
     ligne, plus les vidéos et notices de l'appareil reliées à aucune ligne —
     résolus par le serveur. Les vidéos ne se chargent qu'au clic ; l'acte garde
     la trace de ce qui était proposé (`supportsProposes`, calculé au serveur). */
  const supports = supportsDeLaFenetre(ligne, occ);
  /* LES DOCUMENTS AVEC LA DÉCLARATION (29/10/2026) : rapport, bon, facture —
     choisis ici, chacun avec son type, ils partent une fois l'acte créé. */
  const [fichiers, setFichiers] = useState<File[]>([]);
  const [typesDoc, setTypesDoc] = useState<string[]>([]);
  const entreeDoc = useRef<HTMLInputElement>(null);
  /* ⚠️ TROIS ÉTATS PAR OPÉRATION (Inès, 28/10/2026) : faite, non faite, ou
     pas encore dite. Une opération non faite porte son motif — exigé aussi par
     le moteur. */
  const [etats, setEtats] = useState<Record<string, 'fait' | 'non'>>({});
  const [motifs, setMotifs] = useState<Record<string, string>>({});
  const faites = operations.filter((o) => etats[o] === 'fait');
  /* ⚠️ UNE ÉCHÉANCE PASSÉE PART SANS DATE (`datePreremplie`) : seul celui qui
     a fait le geste sait quand. Même règle pour toutes les portes (EF-012). */
  const passee = occ.date < aujourdhuiIso();
  const [dateFaite, setDateFaite] = useState(() => datePreremplie(occ.date, aujourdhuiIso()));
  const [commentaire, setCommentaire] = useState('');
  const [anomalie, setAnomalie] = useState(false);
  /* Q6 : la NC ouverte depuis cette fenêtre — sa référence part avec la déclaration. */
  const [ncOuverte, setNcOuverte] = useState(false);
  const [ncRef, setNcRef] = useState('');

  const nonDites = operations.filter((o) => !etats[o]).length;
  const nonFaites = operations.filter((o) => etats[o] === 'non');
  const sansMotif = nonFaites.filter((o) => !(motifs[o] || '').trim()).length;
  const pose = (o: string, v: 'fait' | 'non') => setEtats((e) => ({ ...e, [o]: v }));
  const bloque = issue === 'non_realisee' ? refusMotifCloture(motifNonRealisee)
    : reprise && !motifReprise.trim() ? 'Une reprise exige son motif : d’où vient cet acte (registre papier, date de retranscription).'
    : !dateFaite ? 'Indiquez la date de réalisation.'
    : nonDites ? `${nonDites} opération(s) à déclarer faite ou non faite.`
      : sansMotif ? `${sansMotif} opération(s) non faite(s) sans motif.`
        : fichiers.some((_, i) => !typesDoc[i]) ? 'Dites le type de chaque document joint.' : '';
  const nc = proposerNC(anomalie, nonFaites.length)
    ? ncPreremplie(occ, {
      anomalie, commentaire, dateFaite,
      nonFaites: nonFaites.map((o) => ({ operation: o, motif: (motifs[o] || '').trim() })),
    }, aujourdhuiIso())
    : null;

  const envoyer = () => run('declarer', async () => {
    if (issue === 'non_realisee') {
      const c = await smqData<{ documentId?: string }>(`ged/maintenances/${occ.planId}/cloturer`, {
        datePrevue: occ.date, motif: motifNonRealisee.trim(),
      }, 'Échéance close « non réalisée » — elle rejoint le registre des non réalisées.');
      if (c) { onFait(); onFerme(); }
      return;
    }
    const r = await smqData<{ documentId?: string }>(`ged/maintenances/${occ.planId}/acquitter`, {
      datePrevue: occ.date, dateFaite, operations: faites, commentaire, anomalie,
      motifs: Object.fromEntries(nonFaites.map((o) => [o, (motifs[o] || '').trim()])),
      ...(ncRef ? { ncRef } : {}),
      ...(reprise ? { reprise: true, motifReprise: motifReprise.trim(), ...(repriseParKey ? { parKey: repriseParKey } : {}) } : {}),
    }, reprise ? 'Maintenance saisie en reprise.' : 'Maintenance déclarée faite.');
    if (!r) return;
    // L'acte existe : un document refusé ne le défait pas, le magasin dit lequel (toast).
    if (r.documentId) for (const [i, f] of fichiers.entries()) await joindreDocumentActe(r.documentId, f, typesDoc[i]);
    onFait(); onFerme();
  });

  return (
    <Modale titre={occ.libelle} largeur={640} onClose={onFerme}
      sousTitre={`${occ.maintenanceRef} · ${occ.equipementCode} — ${occ.equipementNom} · ${occ.frequenceLibelle}${occ.heure ? ` · ${occ.heure}` : ''}`}
      pied={(
        <>
          <Bouton taille="sm" onClick={onFerme}>Annuler</Bouton>
          <Bouton variante={issue === 'non_realisee' ? 'danger' : 'primaire'} taille="sm" onClick={envoyer} disabled={busy === 'declarer' || !!bloque} title={bloque || undefined}>
            <I n="check" s={13} />{issue === 'non_realisee' ? 'Clore — non réalisée' : reprise ? 'Enregistrer la reprise' : 'Déclarer faite'}
          </Bouton>
        </>
      )}>
      <div style={{ display: 'grid', gap: 14 }}>

        <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--surface-sunken)', fontSize: 12.5, display: 'grid', gap: 4 }}>
          <div>Échéance du <strong style={{ fontFamily: 'var(--font-mono)' }}>{fmtDate(occ.date)}</strong></div>
          <div style={{ color: 'var(--text-muted)' }}>
            Qui en répond : {occ.responsableLibelle || 'personne n’est désigné pour ce jour-là'}
          </div>
          {/* Quand l'échéance a été déplacée, on le DIT ici : c'est le moment où
              quelqu'un peut contester le report, pas trois mois plus tard. */}
          {occ.deplacee && <div style={{ color: 'var(--text-muted)' }}>{occ.motifReport}</div>}
          <div style={{ color: 'var(--text-muted)' }}>{occ.motifEtat}</div>
          {occ.ligneActive === false && (
            <div style={{ color: 'var(--amber-600)' }}>
              Cette ligne a été retirée du plan : ses échéances d’avant restent à déclarer faites ou à clore « non réalisée ».
            </div>
          )}
        </div>

        {/* L'ISSUE : faite, ou close sans être faite (Q5). Montrée à qui peut clore. */}
        {clotureOuverte && (
          <div role="radiogroup" aria-label="Issue de l’échéance" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12.5 }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
              <input type="radio" name="issue-echeance" checked={issue === 'faite'} onChange={() => setIssue('faite')} />Elle a été faite
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
              <input type="radio" name="issue-echeance" checked={issue === 'non_realisee'} onChange={() => setIssue('non_realisee')} />Elle n’a pas été faite — la clore « non réalisée »
            </label>
          </div>
        )}

        {issue === 'non_realisee' ? (
          <div>
            <label style={lbl} htmlFor="mnt-motif-nr">Pourquoi n’a-t-elle pas été faite ? *</label>
            <textarea id="mnt-motif-nr" value={motifNonRealisee} onChange={(e) => setMotifNonRealisee(e.target.value)} rows={3}
              maxLength={2000} style={{ ...inputStyle, resize: 'vertical' }}
              placeholder="Ex. appareil prêté au site de Sahline du 12 au 20 ; ligne remplacée par la trimestrielle." />
            <p style={{ margin: '5px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
              L’échéance n’est pas effacée : elle est close avec ce motif, à votre nom, et figure au registre des non réalisées.
              Elle ne pourra plus être déclarée faite (une erreur se corrige depuis l’historique).
            </p>
          </div>
        ) : (<>

        {/* LA REPRISE (EF-015) : une saisie a posteriori, pour soi ou pour autrui. */}
        {gereParc && (
          <div style={{ display: 'grid', gap: 8 }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, cursor: 'pointer' }}>
              <input type="checkbox" checked={reprise} onChange={(e) => setReprise(e.target.checked)} style={{ marginTop: 2 }} />
              <span>Saisie en reprise — retranscrite après coup (registre papier), éventuellement pour la personne qui l’a faite.</span>
            </label>
            {reprise && (
              <div style={{ display: 'grid', gap: 8, padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--surface-sunken)' }}>
                <div>
                  <label style={lbl} htmlFor="mnt-reprise-par">Faite par</label>
                  <Selecteur id="mnt-reprise-par" value={repriseParKey} onChange={setRepriseParKey} style={inputStyle}>
                    <option value="">Moi-même</option>
                    {personnes.map((p) => <option key={p.cle} value={p.cle}>{p.nom}</option>)}
                  </Selecteur>
                </div>
                <div>
                  <label style={lbl} htmlFor="mnt-reprise-motif">Motif de la reprise *</label>
                  <input id="mnt-reprise-motif" value={motifReprise} onChange={(e) => setMotifReprise(e.target.value)} maxLength={2000}
                    placeholder="Ex. retranscription du classeur ENR.J1.01, semaine du 12/10." style={inputStyle} />
                </div>
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                  L’acte portera les deux noms — qui a fait, qui a saisi — et la mention « reprise » ; le journal la nomme.
                </span>
              </div>
            )}
          </div>
        )}

        <ListeSupports supports={supports} origine={occ.equipementCode} />

        <div>
          <label style={lbl} htmlFor="mnt-datefaite">Réalisée le</label>
          <input id="mnt-datefaite" type="date" value={dateFaite} max={aujourdhuiIso()}
            onChange={(e) => setDateFaite(e.target.value)} style={{ ...inputStyle, maxWidth: 200 }} />
          {passee && (
            <p style={{ margin: '5px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
              Échéance du {fmtDate(occ.date)} : indiquez le jour du geste, pas celui de la saisie.
            </p>
          )}
        </div>

        {operations.length > 0 && (
          <div>
            <span style={lbl}>Opérations ({faites.length}/{operations.length} faites{nonFaites.length ? `, ${nonFaites.length} non faite(s)` : ''})</span>
            {/* Lot 5 (11/08/2026) — la routine déclare tout d'un geste ; le
                geste inverse (tout effacer) existe pour repartir de zéro. */}
            <div style={{ display: 'flex', gap: 8, margin: '2px 0 6px' }}>
              <Bouton taille="sm" type="button" onClick={() => setEtats(Object.fromEntries(operations.map((o) => [o, 'fait'])))}>
                Tout déclarer fait
              </Bouton>
              {Object.keys(etats).length > 0 && (
                <Bouton taille="sm" type="button" onClick={() => setEtats({})}>
                  Tout effacer
                </Bouton>
              )}
            </div>
            <ol style={{ display: 'grid', gap: 6, margin: 0, paddingInlineStart: 22 }}>
              {operations.map((o) => (
                <li key={o} style={{ fontSize: 12.5 }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <span style={{ flex: '1 1 260px', color: etats[o] === 'non' ? 'var(--red-600)' : 'inherit' }}>{o}</span>
                    <span role="radiogroup" aria-label={o} style={{ display: 'flex', gap: 12, whiteSpace: 'nowrap' }}>
                      <label style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}>
                        <input type="radio" name={`op-${o}`} checked={etats[o] === 'fait'} onChange={() => pose(o, 'fait')} />faite
                      </label>
                      <label style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}>
                        <input type="radio" name={`op-${o}`} checked={etats[o] === 'non'} onChange={() => pose(o, 'non')} />non faite
                      </label>
                    </span>
                  </div>
                  {etats[o] === 'non' && (
                    <input value={motifs[o] || ''} onChange={(e) => setMotifs((m) => ({ ...m, [o]: e.target.value }))}
                      aria-label={`Pourquoi « ${o} » n’a pas été faite`} maxLength={500} autoFocus
                      placeholder="Pourquoi n’a-t-elle pas été faite ? (obligatoire)"
                      style={{ ...inputStyle, marginTop: 4, borderColor: (motifs[o] || '').trim() ? undefined : 'var(--amber-600)' }} />
                  )}
                </li>
              ))}
            </ol>
            {bloque && (
              <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--amber-600)' }}>
                {bloque} Une opération non faite est enregistrée telle quelle, avec son motif — c’est ce qu’un évaluateur viendra lire.
              </p>
            )}
          </div>
        )}

        <div>
          <label style={lbl} htmlFor="mnt-comm">Commentaire</label>
          <textarea id="mnt-comm" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} rows={3}
            style={{ ...inputStyle, resize: 'vertical' }} placeholder="Ce qui a été constaté, ce qui n’a pas pu être fait…" />
        </div>

        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, cursor: 'pointer' }}>
          <input type="checkbox" checked={anomalie} onChange={(e) => setAnomalie(e.target.checked)} style={{ marginTop: 2 }} />
          <span>Cette maintenance a révélé une anomalie.</span>
        </label>

        {/* Q6 (Ines, 03/11/2026) : une anomalie, ou une opération non faite,
            fait PROPOSER une NC préremplie — la personne décide. Le formulaire
            est celui du registre des NC ; sa référence part avec la déclaration. */}
        {(nc || ncRef) && (
          <div style={{ padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--surface-sunken)', fontSize: 12.5, display: 'grid', gap: 6 }}>
            {ncRef ? (
              <span>
                <I n="check" s={13} /> Non-conformité <strong style={{ fontFamily: 'var(--font-mono)' }}>{ncRef}</strong> ouverte :
                elle sera reliée à cette maintenance.
              </span>
            ) : (
              <>
                <span>
                  {anomalie ? 'Une anomalie est signalée' : `${nonFaites.length} opération(s) non faite(s)`} :
                  faut-il ouvrir une non-conformité ? Elle sera reliée à cette maintenance.
                </span>
                <div>
                  <Bouton taille="sm" type="button" onClick={() => setNcOuverte(true)}>
                    <I n="plus" s={13} />Ouvrir une NC
                  </Bouton>
                </div>
              </>
            )}
          </div>
        )}

        <div style={{ display: 'grid', gap: 6 }}>
          <span style={lbl}>Documents (rapport, bon d’intervention, facture…)</span>
          {fichiers.map((f, i) => (
            <span key={`${f.name}-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, flexWrap: 'wrap' }}>
              <I n="doc" s={13} />{f.name}
              <Selecteur aria-label={`Type du document ${f.name}`} value={typesDoc[i] || ''}
                onChange={(v) => setTypesDoc((l) => l.map((t, j) => (j === i ? v : t)))}
                style={{ width: 'auto', minWidth: 200, ...(typesDoc[i] ? {} : { color: 'var(--amber-600)' }) }}>
                <option value="">— type du document —</option>
                {TYPES_DOCUMENT.map((t) => <option key={t.k} value={t.k}>{t.label}</option>)}
              </Selecteur>
              <Bouton taille="sm" variante="discret" aria-label={`Retirer ${f.name}`} title="Retirer ce fichier (rien n’est encore envoyé)"
                onClick={() => { setFichiers((l) => l.filter((_, j) => j !== i)); setTypesDoc((l) => l.filter((_, j) => j !== i)); }}>
                <I n="x" s={12} />
              </Bouton>
            </span>
          ))}
          <input ref={entreeDoc} type="file" accept=".pdf,.jpg,.jpeg,.png" multiple style={{ display: 'none' }}
            onChange={(e) => {
              const liste = Array.from(e.target.files || []);
              if (liste.length) { setFichiers((a) => [...a, ...liste]); setTypesDoc((a) => [...a, ...liste.map(() => '')]); }
              e.target.value = '';
            }} />
          <div>
            <Bouton taille="sm" type="button" onClick={() => entreeDoc.current?.click()}>
              <I n="plus" s={13} />{fichiers.length ? 'Ajouter un fichier' : 'Joindre des documents'}
            </Bouton>
          </div>
        </div>
        </>)}

      </div>
      {ncOuverte && nc && (
        <Modale titre={`Non-conformité — ${occ.equipementCode} · ${occ.libelle}`} largeur={900} onClose={() => setNcOuverte(false)}>
          <DeclareForm encadre={false} initial={nc.initial} objetsInitiaux={nc.objets}
            contexte={{ source: 'equipement', equipementCode: occ.equipementCode }}
            onDone={(ref) => { setNcOuverte(false); if (ref) setNcRef(ref); }} />
        </Modale>
      )}
    </Modale>
  );
}

/**
 * L'HÔTE de la fenêtre, monté une fois par le Shell : il lit la demande posée
 * par `ouvrirDeclaration`, retrouve l'échéance (fournie par l'appelant, sinon
 * dans le plan chargé), et refuse ce qui ne se déclare pas — une échéance à
 * venir ou déjà déclarée — avec la même règle que le serveur.
 */
export function HoteDeclaration() {
  const demande = useGed((s) => s.declaration);
  if (!demande) return null;
  // La clé remonte la fenêtre à chaque nouvelle demande : rien d'une saisie
  // précédente ne survit (cases, date, pièces).
  return <Declaration key={demande.ref} demande={demande} />;
}

function Declaration({ demande }: { demande: DemandeDeclaration }) {
  const maintenances = useGed((s) => s.maintenances);
  const { fermerDeclaration, loadMaintenances, showToast } = useGed();
  const relu = useRef(false);

  const occ = useMemo(() => demande.occ
    || (maintenances?.occurrences || []).find((o) => designe(o, demande.ref)), [demande, maintenances]);
  const ligne = demande.ligne || (occ ? (maintenances?.lignes || []).find((l) => l.documentId === occ.planId) : undefined);

  const fermer = React.useCallback(() => { demande.aLaFermeture?.(); fermerDeclaration(); }, [demande, fermerDeclaration]);

  useEffect(() => {
    if (occ && declarable(occ)) return;
    if (!occ && !relu.current) {
      // Le plan en mémoire peut ne pas porter l'échéance : on le relit une fois.
      relu.current = true;
      void loadMaintenances();
      return;
    }
    if (!occ && !maintenances) return;
    showToast(!occ
      ? 'Cette échéance est introuvable dans le plan de maintenance.'
      : occ.etat === 'a_venir'
        ? `L’échéance du ${fmtDate(occ.date)} est à venir : elle se déclare faite à partir de ce jour-là.`
        : occ.etat === 'suspendue'
          ? `L’échéance du ${fmtDate(occ.date)} est suspendue : l’appareil était hors service ce jour-là.`
          : occ.etat === 'non_realisee'
            ? `L’échéance du ${fmtDate(occ.date)} a été close « non réalisée ».`
            : `L’échéance du ${fmtDate(occ.date)} est déjà déclarée faite.`, 'erreur');
    fermer();
  }, [occ, maintenances, loadMaintenances, showToast, fermer]);

  if (!occ || !declarable(occ)) return null;
  return (
    <DeclarerFaite occ={occ} ligne={ligne} operationsConnues={demande.operationsConnues}
      onFerme={fermer}
      onFait={() => {
        useGed.setState((s) => ({ declarationsFaites: s.declarationsFaites + 1 }));
        void demande.apres?.();
        void loadMaintenances();
      }} />
  );
}
